import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import FooterSection from '../components/FooterSection';
import SEO from '../components/SEO';
import { LanguageProvider } from '../context/LanguageContext';
import {
 Monitor,
 Utensils,
 Coffee,
 LayoutGrid,
 Printer,
 SplitSquareHorizontal,
 QrCode,
 Users,
 Tablet,
 Zap,
 CreditCard,
 ArrowRight,
 Sparkles,
 Clock,
 CheckCircle2,
 AlertCircle,
 Mail,
 Store,
 ShieldCheck,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Roadmap features — explicitly NOT yet available. Each carries an honest
// status badge so we never overpromise active functionality.
const ROADMAP = [
 { icon: Utensils, title: 'Dine-in mode', desc: 'Servers take orders tableside on iPad with a premium floor-aware interface.', wave: 'Q3 2026' },
 { icon: Coffee, title: 'Counter mode', desc: 'Quick-service flow optimized for cafés, bars, and counter-only restaurants.', wave: 'Q3 2026' },
 { icon: LayoutGrid, title: 'Smart table management', desc: 'Visual floor plan, table status, guest counts, and seat-level orders.', wave: 'Q3 2026' },
 { icon: QrCode, title: 'QR table payments', desc: 'Customers pay from their phone via a QR linked to their table check.', wave: 'Q3 2026' },
 { icon: SplitSquareHorizontal, title: 'Split payments', desc: 'Split by item, by seat, or evenly across the table — no friction.', wave: 'Q4 2026' },
 { icon: Users, title: 'Employee login & roles', desc: 'PIN logins, server attribution, manager overrides, shift tracking.', wave: 'Q4 2026' },
 { icon: Zap, title: 'Real-time KDS', desc: 'Kitchen Display System with live order routing — no more paper bottlenecks.', wave: 'Q4 2026' },
 { icon: Printer, title: 'Multi-printer routing', desc: 'Send orders to bar, kitchen, and counter printers independently.', wave: 'Q4 2026' },
 { icon: Monitor, title: 'Daily operations dashboard', desc: 'End-of-day reports, shift summaries, server performance — all live.', wave: 'Q4 2026' },
];

// Today already live in NoFee Ordering (no need to wait for POS Lite)
const ALREADY_LIVE = [
 'Online ordering & digital menu',
 'QR ordering for customers',
 'Printable receipts & kitchen tickets',
 'Apple Pay · Google Pay · Stripe',
 'Direct restaurant payouts',
 'Order status tracking & notifications',
 'iPad / tablet-friendly dashboard',
];

const NoFeePOSLite = () => {
 const [form, setForm] = useState({
 email: '',
 restaurant_name: '',
 name: '',
 num_locations: '1',
 });
 const [submitting, setSubmitting] = useState(false);
 const [done, setDone] = useState(false);
 const [error, setError] = useState('');
 const [waitlistCount, setWaitlistCount] = useState(null);

 useEffect(() => {
 axios.get(`${API}/pos-lite/waitlist/count`)
.then((r) => setWaitlistCount(r.data?.count?? null))
.catch(() => {});
 }, []);

 const submit = async (e) => {
 e.preventDefault();
 setError('');
 if (!form.email.trim() ||!form.email.includes('@')) {
 setError('Please enter a valid email.');
 return;
 }
 setSubmitting(true);
 try {
 await axios.post(`${API}/pos-lite/waitlist`, {
...form,
 source: 'for-restaurants/pos-lite',
 });
 setDone(true);
 // Optimistic count bump
 setWaitlistCount((c) => (typeof c === 'number'? c + 1: c));
 } catch (err) {
 setError(err?.response?.data?.detail || 'Could not save your request. Please try again.');
 } finally {
 setSubmitting(false);
 }
 };

 return (
 <LanguageProvider>
 <SEO
 title="NoFee POS Lite — Restaurant Point-of-Sale Companion (Coming Soon)"
 description="NoFee POS Lite — the lightweight POS companion that pairs with your 0% commission NoFee menu. Sync orders, tickets, and payouts in one dashboard. Join the waitlist."
 path="/for-restaurants/pos-lite"
 />
 <div className="min-h-screen bg-black text-white">
 <Navbar />

 {/* Hero */}
 <section className="relative pt-32 sm:pt-36 lg:pt-44 pb-16 lg:pb-20">
 <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(212,175,55,0.10),transparent_60%)]" />
 <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
 <Link
 to="/for-restaurants"
 className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-gold-300 transition-colors mb-6"
 data-testid="back-to-solutions-pos"
 >
 ← Back to Restaurant Solutions
 </Link>

 <div className="flex flex-wrap items-center gap-2 mb-6">
 <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold-500/30 bg-gold-500/5">
 <Monitor className="w-3.5 h-3.5 text-gold-300" />
 <span className="text-[11px] uppercase tracking-[0.22em] text-gold-300 font-semibold">
 NoFee POS Lite
 </span>
 </div>
 <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/[0.06]">
 <Clock className="w-3 h-3 text-emerald-300" />
 <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-bold">
 Early Access · Coming 2026
 </span>
 </div>
 </div>

 <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] max-w-3xl">
 The modern POS
 <br />
 <span className="bg-gradient-to-r from-amber-200 via-gold-400 to-amber-300 bg-clip-text text-transparent">
 we're building next.
 </span>
 </h1>
 <p className="mt-6 max-w-2xl text-base sm:text-lg text-white/65 leading-relaxed">
 NoFee POS Lite is our upcoming operational platform — dine-in, counter,
 table management, split payments, and KDS, all on the iPad and tablets
 you already own. Join the waitlist for early access pricing and product
 previews.
 </p>

 <div className="mt-8 flex flex-col sm:flex-row gap-3">
 <a
 href="#waitlist"
 data-testid="pos-join-waitlist-cta"
 className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black font-bold transition-all hover:shadow-[0_15px_40px_-15px_rgba(212,175,55,0.6)]"
 >
 Join the Waitlist
 <ArrowRight className="w-4 h-4" />
 </a>
 <Link
 to="/for-restaurants/ordering"
 className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl border border-white/15 hover:border-gold-500/40 text-white/85 hover:text-white font-medium transition-all"
 data-testid="pos-see-ordering"
 >
 Get NoFee Ordering today
 </Link>
 </div>

 {typeof waitlistCount === 'number' && waitlistCount > 0 && (
 <p className="mt-4 text-xs text-white/45" data-testid="waitlist-social-proof">
 <span className="text-gold-400 font-bold">{waitlistCount.toLocaleString()}</span> restaurants already on the waitlist.
 </p>
 )}
 </div>
 </section>

 {/* What's already live (no waiting needed) */}
 <section className="relative pb-16 lg:pb-20 border-t border-white/[0.05]">
 <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pt-16">
 <div className="rounded-3xl border border-emerald-500/20 bg-emerald-500/[0.03] p-6 sm:p-8">
 <div className="flex items-start gap-4">
 <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
 <CheckCircle2 className="w-5 h-5 text-emerald-300" />
 </div>
 <div className="flex-1 min-w-0">
 <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-300 font-bold mb-1">
 Available today
 </p>
 <h2 className="text-xl sm:text-2xl font-black tracking-tight text-white">
 Don't want to wait? NoFee Ordering ships everything below right now.
 </h2>
 <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
 {ALREADY_LIVE.map((f) => (
 <li key={f} className="flex items-start gap-2 text-[14px] text-white/75">
 <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-1" />
 <span>{f}</span>
 </li>
 ))}
 </ul>
 <Link
 to="/for-restaurants/ordering"
 data-testid="pos-cta-ordering-live"
 className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-white/15 hover:border-emerald-400/40 text-white text-[13px] font-semibold transition-all"
 >
 Start with NoFee Ordering — $299/mo
 <ArrowRight className="w-3.5 h-3.5" />
 </Link>
 </div>
 </div>
 </div>
 </div>
 </section>

 {/* Roadmap */}
 <section className="relative pb-16 lg:pb-20 border-t border-white/[0.05]">
 <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pt-16">
 <div className="max-w-2xl mb-12">
 <p className="text-[11px] uppercase tracking-[0.28em] text-gold-400 font-semibold mb-3">
 Public roadmap
 </p>
 <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
 What POS Lite will unlock.
 </h2>
 <p className="mt-3 text-white/60">
 We're building POS Lite in public. Every feature below is in active
 design or development — none are live yet. Waitlist members get
 first access as each wave ships.
 </p>
 </div>

 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
 {ROADMAP.map((f) => (
 <div
 key={f.title}
 className="group relative rounded-2xl border border-white/10 bg-zinc-950/60 p-6 hover:border-gold-500/30 transition-all duration-300"
 data-testid={`pos-roadmap-${f.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
 >
 <span className="absolute top-4 right-4 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/25 text-[10px] font-bold tracking-wide text-amber-300">
 {f.wave}
 </span>
 <div className="w-10 h-10 rounded-xl bg-gold-500/10 border border-gold-500/20 flex items-center justify-center mb-4">
 <f.icon className="w-4.5 h-4.5 text-gold-300" />
 </div>
 <h3 className="text-base font-bold text-white pr-16">{f.title}</h3>
 <p className="mt-1.5 text-[13.5px] text-white/55 leading-relaxed">
 {f.desc}
 </p>
 </div>
 ))}
 </div>

 <p className="mt-8 text-center text-[12px] text-white/40 max-w-2xl mx-auto">
 <AlertCircle className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
 Dates are targets, not guarantees. We ship when each module is genuinely
 production-ready — never before.
 </p>
 </div>
 </section>

 {/* Hardware reassurance */}
 <section className="relative pb-16 lg:pb-20 border-t border-white/[0.05]">
 <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pt-16">
 <div className="rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-950 to-black p-8 sm:p-12">
 <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-10 items-center">
 <div>
 <p className="text-[11px] uppercase tracking-[0.28em] text-gold-400 font-semibold mb-3">
 No expensive hardware
 </p>
 <h3 className="text-2xl sm:text-3xl font-black tracking-tight">
 Bring your own iPad. Skip the $3,000 POS upsell.
 </h3>
 <p className="mt-4 text-white/60 max-w-md">
 Legacy POS vendors lock restaurants into proprietary terminals and
 multi-year contracts. NoFee POS Lite is being built to run on the
 iPad, Android tablet, or Windows device you already own.
 </p>
 </div>
 <ul className="space-y-3">
 {[
 'Designed for iPad, Android & Windows tablets',
 'Compatible with standard thermal receipt printers',
 'Zero long-term hardware contracts',
 'No proprietary cards or terminals required',
 'Free remote updates — never obsolete',
 ].map((line) => (
 <li key={line} className="flex items-start gap-3 text-[14.5px] text-white/80">
 <CheckCircle2 className="w-4 h-4 text-gold-400 flex-shrink-0 mt-1" />
 <span>{line}</span>
 </li>
 ))}
 </ul>
 </div>
 </div>
 </div>
 </section>

 {/* Waitlist form */}
 <section
 id="waitlist"
 className="relative pb-24 lg:pb-32 border-t border-white/[0.05] scroll-mt-24"
 >
 <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pt-16">
 <div className="max-w-2xl mx-auto">
 <div className="text-center mb-8">
 <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/[0.06] mb-4">
 <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
 <span className="text-[11px] uppercase tracking-[0.22em] text-emerald-300 font-bold">
 Early Access
 </span>
 </div>
 <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-white">
 Be first when POS Lite ships.
 </h2>
 <p className="mt-3 text-white/60">
 Waitlist members get early access invites, founding-restaurant
 pricing, and a direct line to the team building it.
 </p>
 </div>

 {done? (
 <div
 className="rounded-3xl border border-emerald-500/30 bg-emerald-500/[0.06] p-8 sm:p-10 text-center"
 data-testid="waitlist-success"
 >
 <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/30 mb-4">
 <CheckCircle2 className="w-7 h-7 text-emerald-300" />
 </div>
 <h3 className="text-2xl font-black tracking-tight text-white">
 You're on the list.
 </h3>
 <p className="mt-2 text-white/65">
 We'll email <span className="text-gold-300 font-semibold">{form.email}</span> as soon as early access opens. In the meantime, start with NoFee Ordering — no waiting required.
 </p>
 <Link
 to="/for-restaurants/ordering"
 className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-white/15 hover:border-gold-400/40 text-white font-semibold text-sm transition-all"
 data-testid="waitlist-success-ordering"
 >
 Start NoFee Ordering today
 <ArrowRight className="w-3.5 h-3.5" />
 </Link>
 </div>
 ): (
 <form
 onSubmit={submit}
 className="rounded-3xl border border-white/10 bg-zinc-950 p-6 sm:p-8 space-y-4"
 data-testid="waitlist-form"
 >
 {error && (
 <div className="p-3 bg-rose-500/10 text-rose-200 text-sm rounded-xl border border-rose-500/25 flex items-start gap-2">
 <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
 <span>{error}</span>
 </div>
 )}

 <div className="relative">
 <Mail className="w-4 h-4 text-white/40 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
 <input
 type="email"
 required
 value={form.email}
 onChange={(e) => setForm({...form, email: e.target.value })}
 placeholder="Email address"
 className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-black/40 border border-white/10 text-white placeholder:text-white/30 focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none transition-all text-[15px]"
 data-testid="waitlist-email"
 autoComplete="email"
 />
 </div>

 <div className="relative">
 <Store className="w-4 h-4 text-white/40 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
 <input
 type="text"
 value={form.restaurant_name}
 onChange={(e) => setForm({...form, restaurant_name: e.target.value })}
 placeholder="Restaurant name (optional)"
 className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-black/40 border border-white/10 text-white placeholder:text-white/30 focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none transition-all text-[15px]"
 data-testid="waitlist-restaurant"
 />
 </div>

 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <input
 type="text"
 value={form.name}
 onChange={(e) => setForm({...form, name: e.target.value })}
 placeholder="Your name (optional)"
 className="w-full px-4 py-3.5 rounded-xl bg-black/40 border border-white/10 text-white placeholder:text-white/30 focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none transition-all text-[15px]"
 data-testid="waitlist-name"
 autoComplete="name"
 />
 <select
 value={form.num_locations}
 onChange={(e) => setForm({...form, num_locations: e.target.value })}
 className="w-full px-4 py-3.5 rounded-xl bg-black/40 border border-white/10 text-white focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none transition-all text-[15px]"
 data-testid="waitlist-locations"
 >
 <option value="1">1 location</option>
 <option value="2-5">2–5 locations</option>
 <option value="6-20">6–20 locations</option>
 <option value="20+">20+ locations</option>
 </select>
 </div>

 <button
 type="submit"
 disabled={submitting}
 data-testid="waitlist-submit"
 className="w-full inline-flex items-center justify-center gap-2 py-4 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 active:from-amber-400 active:to-gold-600 text-black font-extrabold text-[15px] transition-all shadow-[0_15px_40px_-15px_rgba(212,175,55,0.6)] disabled:opacity-60"
 >
 {submitting? 'Joining…': 'Notify Me When Available'}
 {!submitting && <ArrowRight className="w-4 h-4" />}
 </button>

 <p className="text-center text-[11px] text-white/40 flex items-center justify-center gap-1.5">
 <ShieldCheck className="w-3 h-3" />
 No spam. No commitment. Unsubscribe anytime.
 </p>
 </form>
 )}
 </div>
 </div>
 </section>

 <FooterSection />
 </div>
 </LanguageProvider>
 );
};

export default NoFeePOSLite;
